/**
 * Copyright © 2026 Adnuntius AS.
 */
import {AdnListHelperService} from '../../common/controller/list-helper.service';
import {ChangeDetectorRef, Component, Inject, NgZone, OnChanges} from '@angular/core';
import {deferMarkForCheck} from '../../common/util/defer-mark-for-check';
import * as _ from 'lodash';
import template from './external-demand-source-edit-page.component.html';
import {AppEventBusService} from '../../common/events/app-event-bus.service';

import {AdminResourceEditPageBase} from '../../common/controller/admin-resource-edit-page-base';
import {CurrenciesResource} from '../../../components/api/resources/resource/currencies';
import {AdnSortingHeaderParam} from '../../common/directives/html/sorting-header.component';
import {CreativeResource} from '../../../components/api/resources/resource/creative.resource';
import {ExternalAdUnitResource} from '../../../components/api/resources/resource/externaladunit.resource';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../../components/session/local-user-permissions';
import {UserProfileSaverService} from '../../../components/session/user-profile-saver.service';

@Component({
  standalone: false,
  selector: 'adn-external-demand-source-edit-page',
  template
})
export class ExternalDemandSourceEditPageComponent extends AdminResourceEditPageBase implements OnChanges {
  readonly externalPartners = ['PREBID_JS'];
  readonly extAdUnitSortParams: AdnSortingHeaderParam[] = [
    'name',
    'externalSiteId',
    'externalAdUnitId',
    'externalAdType',
    {param: 'width', label: 'dimensions'}
  ];
  readonly creativeSortParams: AdnSortingHeaderParam[] = [
    'name',
    {param: 'Layout', label: 'layout'},
    {label: 'dimensions', param: 'width'},
    {param: '', label: 'targeting'}
  ];

  currencies: string[] = [];
  canManageLayouts = false;
  readonly usageResource: any = {
    creativeList: {},
    showExtAdUnitDetail: false,
    showCreativeDetail: false
  };
  /** Snapshots — list-helper replaces usage arrays on settle (NG0100). */
  displayExternalAdUnits: any[] = [];
  hasExternalAdUnits = false;
  externalAdUnitCount = 0;
  displayCreatives: any[] = [];
  hasCreatives = false;
  creativeCount = 0;

  private listInitialized = false;

  constructor(
    eventBus: AppEventBusService,
    @Inject(UserProfileSaverService) UserProfileSaver: any,
    @Inject(LocalNetworkProfile) LocalNetworkProfile: any,
    @Inject(LocalUserPermissions) LocalUserPermissions: any,
    @Inject(ExternalAdUnitResource) private ExternalAdUnit: any,
    @Inject(CreativeResource) private Creative: any,
    private adnListHelper: AdnListHelperService,
    private currenciesResource: CurrenciesResource,
    private cdr: ChangeDetectorRef,
    ngZone: NgZone
  ) {
    super(eventBus, UserProfileSaver, LocalNetworkProfile, ngZone);
    this.canManageLayouts = LocalUserPermissions.getAllVisPermissions()?.MANAGE_LAYOUTS === true;
    this.currenciesResource.get().then((results: any[]) => {
      this.ngZone.run(() => {
        this.currencies = _.map(results, 'currency');
      });
    });
  }

  ngOnChanges(): void {
    super.ngOnChanges();
    if (this.model?.id && !this.listInitialized) {
      this.listInitialized = true;
      const moreParams = {externalDemandSource: this.model.id};
      this.adnListHelper.setUpBasicList(this.usageResource, this.ExternalAdUnit, 'fullExternalAdUnitsForExternalDemandSource', {
        moreParams,
        noInitQuery: true,
        afterFill: () => {
          deferMarkForCheck(this.cdr, () => {
            this.displayExternalAdUnits = (this.usageResource.items || []).slice();
            this.hasExternalAdUnits = this.displayExternalAdUnits.length > 0;
            this.externalAdUnitCount = this.usageResource.totalCount || 0;
          });
        }
      });
      this.adnListHelper.setUpBasicList(this.usageResource.creativeList, this.Creative, 'fullCreativesForExternalDemandSource', {
        moreParams,
        noInitQuery: true,
        afterFill: () => {
          deferMarkForCheck(this.cdr, () => {
            this.displayCreatives = (this.usageResource.creativeList.items || []).slice();
            this.hasCreatives = this.displayCreatives.length > 0;
            this.creativeCount = this.usageResource.creativeList.totalCount || 0;
          });
        }
      });
      if (!this.isNew) {
        this.usageResource.makeQuery?.();
        this.usageResource.creativeList.makeQuery?.();
      }
    }
  }

  protected afterInitEditingModel(): void {
    this.editingModel.externalPartner = this.editingModel.externalPartner || 'PREBID_JS';
  }

  protected afterInitAfterSave(): void {
    if (!this.isNew) {
      this.usageResource.makeQuery?.();
      this.usageResource.creativeList.makeQuery?.();
    }
  }
}
